import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ruler, Loader2, RefreshCw, TrendingUp, TrendingDown, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_INFO = {
  en: {
    "23.6%": "Shallow retracement — strong trends",
    "38.2%": "First major support — Wave 4 target",
    "50%": "Psychological midpoint",
    "61.8%": "Golden Ratio — strongest reversal zone",
    "78.6%": "Last defense before trend invalidation",
    "127.2%": "First minor extension target",
    "161.8%": "Primary profit target — Wave 3 target",
    "200%": "Strong trend continuation",
    "261.8%": "Extended target in powerful trends",
  },
  ar: {
    "23.6%": "ارتداد سطحي — أسواق قوية الاتجاه",
    "38.2%": "أول دعم رئيسي — هدف الموجة 4",
    "50%": "النقطة النفسية الوسطى",
    "61.8%": "النسبة الذهبية — أقوى منطقة انعكاس",
    "78.6%": "آخر دفاع قبل إبطال الاتجاه",
    "127.2%": "أول هدف امتداد",
    "161.8%": "هدف الربح الرئيسي — هدف الموجة 3",
    "200%": "استمرار قوي للاتجاه",
    "261.8%": "هدف ممتد في الاتجاهات القوية",
  },
};

export function FibonacciPage({ lang, symbol = "XAU/USD", timeframe = "4h" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isAr = lang === "ar";

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const urlSymbol = symbol.replace("/", "-");
      const res = await axios.get(`${API}/fibonacci/${urlSymbol}`, { params: { timeframe } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب البيانات" : "Failed to fetch"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [symbol, timeframe]);

  const sigColor = data?.signal === "BUY" ? "bg-emerald-500" : data?.signal === "SELL" ? "bg-red-500" : "bg-zinc-400";
  const TrendIcon = data?.uptrend ? TrendingUp : TrendingDown;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="space-y-4">
      <div className="flex items-center gap-2">
        <Ruler className="w-6 h-6 text-amber-500" />
        <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600">
          {isAr ? "تحليل فيبوناتشي" : "Fibonacci Analysis"}
        </h1>
      </div>

      {/* Live signal */}
      <Card className="bg-white border-4 border-amber-300 rounded-3xl overflow-hidden shadow-lg shadow-amber-200/40">
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-pink-400" />
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-amber-500" />
              <h3 className="font-bold text-sm sm:text-base text-zinc-800">
                {isAr ? "السعر مقابل المستويات" : "Price vs Levels"}
              </h3>
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                {symbol} · {timeframe}
              </Badge>
            </div>
            <Button variant="ghost" size="icon" onClick={fetchData} disabled={loading} className="text-amber-500 h-8 w-8">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>

          {loading && !data && (
            <div className="flex items-center justify-center py-8 text-amber-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm">{isAr ? "حساب المستويات..." : "Computing levels..."}</span>
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs">{error}</div>}

          {data && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 tracking-wide">{isAr ? "أقرب مستوى" : "Nearest Level"}</p>
                  <p className="text-lg sm:text-xl font-extrabold text-amber-700">{data.nearest_level?.name} <span className="text-zinc-500 text-sm">@ {data.nearest_level?.price}</span></p>
                  <p className="text-xs text-zinc-600 mt-0.5">{isAr ? "المسافة:" : "Distance:"} <strong>{data.nearest_level?.distance_pct}%</strong></p>
                </div>
                <div className="text-right">
                  <Badge className={`${sigColor} text-white text-base px-4 py-2 rounded-full font-bold`}>{data.signal}</Badge>
                  <p className="text-xs text-zinc-500 mt-1">{isAr ? "الثقة" : "Confidence"} <strong className="text-pink-600">{data.confidence}%</strong></p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-zinc-600 mb-3 bg-amber-50 rounded-xl p-2.5 border border-amber-200">
                <TrendIcon className={`w-4 h-4 ${data.uptrend ? "text-emerald-600" : "text-red-500"}`} />
                <span>
                  {isAr ? "نمط:" : "Pattern:"} <strong>{data.uptrend ? (isAr ? "صاعد" : "Uptrend") : (isAr ? "هابط" : "Downtrend")}</strong> ·
                  {" "}High: <strong className="font-mono">{data.swing_high}</strong> · Low: <strong className="font-mono">{data.swing_low}</strong>
                </span>
              </div>

              {/* Levels table */}
              <div className="grid grid-cols-2 gap-3">
                <LevelsList title={isAr ? "مستويات الارتداد" : "Retracements"} levels={data.levels} price={data.current_price} info={LEVEL_INFO[lang]} />
                <LevelsList title={isAr ? "مستويات الامتداد" : "Extensions"} levels={data.extensions} price={data.current_price} info={LEVEL_INFO[lang]} />
              </div>

              {data.notes?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-xs text-amber-800 mt-3 space-y-1">
                  {data.notes.map((n, i) => <p key={i}>• {n}</p>)}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Reference */}
      <Card className="bg-white border-2 border-pink-200 rounded-2xl">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-pink-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">{isAr ? "مرجع المستويات" : "Levels Reference"}</h3>
          </div>
          <div className="space-y-2 text-xs sm:text-sm">
            {Object.entries(LEVEL_INFO[lang]).map(([level, desc]) => (
              <div key={level} className="flex items-start gap-2">
                <Badge className="bg-pink-100 text-pink-700 border-0 font-mono shrink-0">{level}</Badge>
                <span className="text-zinc-700">{desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-xs text-zinc-500 py-3">
        <Ruler className="w-4 h-4 inline mr-1 text-amber-400" />
        {isAr ? "61.8% هي النسبة الذهبية — أقوى نقطة انعكاس" : "61.8% is the Golden Ratio — strongest reversal zone"}
      </div>
    </div>
  );
}

function LevelsList({ title, levels, price, info }) {
  return (
    <div>
      <p className="text-xs font-bold text-zinc-700 mb-2">{title}</p>
      <div className="space-y-1">
        {Object.entries(levels).map(([k, v]) => {
          const isNear = Math.abs(v - price) / price * 100 < 0.3;
          return (
            <div
              key={k}
              className={`flex items-center justify-between text-xs rounded-lg px-2 py-1.5 border ${
                isNear ? "bg-amber-100 border-amber-300 font-bold" : "bg-zinc-50 border-zinc-100"
              }`}
              title={info[k]}
            >
              <span className="font-mono text-zinc-700">{k}</span>
              <span className="font-mono text-zinc-900">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
